import React from "react";
import { components } from "react-select";
import Avatar from "components/common/Avatar";

function pickString(...vals: Array<unknown>): string {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

export type EmployeeOptionAttributes = {
  value: number;
  label: string;
  /** Present when `value` is an employee id (GET /employees/options). */
  userId?: number;
  user?: {
    avatar?: string;
  };
  /** Present when `value` is a user id (e.g. with-employee-profile); optional metadata. */
  employeeId?: number;
  code?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  email?: string | null;
  userEmail?: string | null;
  department?: { id: number; name: string } | null;
  designation?: { id: number; name: string } | null;
  employmentCategory?: { id: number; name: string } | null;
};

function getEmployeeDisplayName(opt: EmployeeOptionAttributes) {
  const joined = [opt.firstName, opt.lastName].filter(Boolean).join(" ").trim();
  return joined || opt.label;
}

function getEmployeeEmail(opt: EmployeeOptionAttributes) {
  return pickString(opt.email, opt.userEmail);
}

export function EmployeeOption(props: any) {
  const { data, innerRef, innerProps, isDisabled } = props;
  const opt = data as EmployeeOptionAttributes;

  const name = getEmployeeDisplayName(opt);
  const email = getEmployeeEmail(opt);
  const code = pickString(opt.code);
  const avatar = pickString(opt.avatar, opt.user?.avatar);
  const department = opt.department?.name ?? "";
  const designation = opt.designation?.name ?? "";
  const orgLine = [designation, department].filter(Boolean).join(" · ");
  const categoryName = opt.employmentCategory?.name ?? "";

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className="d-flex align-items-center gap-2 px-2 py-2"
      style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
    >
      <Avatar
        name={name}
        image={avatar || undefined}
        className="rounded-circle w-100 h-100"
        size="sm"
      />
      <div className="flex-grow-1 min-w-0">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <span className="fw-medium text-dark text-truncate">{name}</span>
          {code ? (
            <span className="badge bg-light text-muted border text-truncate">
              {code}
            </span>
          ) : null}
        </div>
        {orgLine ? (
          <span className="text-muted small text-truncate d-block">
            {orgLine}
          </span>
        ) : email ? (
          <span className="text-muted small text-truncate d-block">
            {email}
          </span>
        ) : null}
        {orgLine && email ? (
          <span className="text-muted small text-truncate d-block">
            {email}
          </span>
        ) : null}
        {categoryName ? (
          <span className="badge bg-secondary-subtle text-secondary mt-1">
            {categoryName}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function EmployeeSingleValue(props: any) {
  const { data } = props;
  const opt = data as EmployeeOptionAttributes;
  const name = getEmployeeDisplayName(opt);
  const email = getEmployeeEmail(opt);
  const code = pickString(opt.code);
  const avatar = pickString(opt.avatar, opt.user?.avatar);
  const department = opt.department?.name ?? "";
  const designation = opt.designation?.name ?? "";
  const orgLine = [designation, department].filter(Boolean).join(" · ");
  const categoryName = opt.employmentCategory?.name ?? "";

  return (
    <components.SingleValue {...props}>
      <div className="d-flex align-items-center gap-2 w-100 overflow-hidden">
        <div className="flex-shrink-0 avatar-sm">
          <Avatar
            name={name}
            image={avatar || undefined}
            className="rounded-circle w-100 h-100"
          />
        </div>
        <div className="min-w-0 text-truncate d-flex flex-column">
          <div className="d-flex align-items-center gap-2 min-w-0">
            <span className="fw-medium text-truncate">{name}</span>
            {code ? (
              <span className="text-muted small text-truncate">{code}</span>
            ) : null}
            {categoryName ? (
              <span className="badge bg-secondary-subtle text-secondary flex-shrink-0">
                {categoryName}
              </span>
            ) : null}
          </div>
          {orgLine ? (
            <span className="text-muted small text-truncate">{orgLine}</span>
          ) : email ? (
            <span className="text-muted small text-truncate">{email}</span>
          ) : null}
        </div>
      </div>
    </components.SingleValue>
  );
}
